import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useMemo, useRef, useEffect, useState } from "react";
import { createHistoryChunkStore } from "./chunkStore";
import { createSingleWriterLane } from "./singleWriterLane";
const BackfillDedupContext = createContext(null);
const MAX_DEDUP_ENTRIES = 200;
export function BackfillDedupProvider({ children }) {
    const mapRef = useRef(new Map());
    const value = useMemo(() => {
        const evictIfNeeded = () => {
            if (mapRef.current.size > MAX_DEDUP_ENTRIES) {
                const firstKey = mapRef.current.keys().next().value;
                if (firstKey !== undefined)
                    mapRef.current.delete(firstKey);
            }
        };
        return {
            register: (k, p) => {
                evictIfNeeded();
                mapRef.current.set(k, p);
                p.finally(() => {
                    if (mapRef.current.get(k) === p) {
                        mapRef.current.delete(k);
                    }
                });
            },
            get: (k) => mapRef.current.get(k),
        };
    }, []);
    return (_jsx(BackfillDedupContext.Provider, { value: value, children: children }));
}
export function useBackfillDedup() {
    return useContext(BackfillDedupContext);
}
/**
 * Run `fn` once per `key`. Concurrent callers with the same key share the same
 * promise. When no dedup context is present (SingleChart standalone), `fn`
 * runs normally.
 */
export function withBackfillDedup(ctx, key, fn) {
    if (!ctx) {
        return fn();
    }
    const existing = ctx.get(key);
    if (existing) {
        return existing;
    }
    const p = fn();
    ctx.register(key, p);
    return p;
}
function keyForBefore(symbol, tf, beforeMs, n) {
    return `${symbol}|${tf}|before|${beforeMs}|${n}`;
}
function keyForRange(symbol, tf, fromMs, toMs) {
    return `${symbol}|${tf}|range|${fromMs}|${toMs}`;
}
/**
 * Wrap an adapter so that `fetchBarsBefore` / `fetchBarsInRange` participate in
 * cross-chart backfill dedup. The dedup key is derived purely from the request
 * arguments (symbol, tf, range) and deliberately EXCLUDES chartId.
 */
export function dedupWrapAdapter(adapter, ctx) {
    return {
        ...adapter,
        fetchBarsBefore: (symbol, tf, before, n, chartId) => withBackfillDedup(ctx, keyForBefore(symbol, tf, before.getTime(), n ?? 0), () => adapter.fetchBarsBefore(symbol, tf, before, n, chartId)),
        fetchBarsInRange: (symbol, tf, from, to, chartId) => withBackfillDedup(ctx, keyForRange(symbol, tf, from.getTime(), to.getTime()), () => adapter.fetchBarsInRange(symbol, tf, from, to, chartId)),
    };
}
const SharedBarStoreContext = createContext(null);
export function SharedBarStoreProvider({ children }) {
    const storesRef = useRef(new Map());
    const value = useMemo(() => ({
        getOrCreate: (key) => {
            let entry = storesRef.current.get(key);
            if (!entry) {
                entry = {
                    store: createHistoryChunkStore(),
                    lane: createSingleWriterLane(),
                    refCount: 0,
                    residents: new Map(),
                };
                storesRef.current.set(key, entry);
            }
            entry.refCount++;
            return entry;
        },
        release: (key, subscriberId) => {
            const entry = storesRef.current.get(key);
            if (!entry)
                return;
            entry.refCount--;
            entry.residents.delete(subscriberId);
            if (entry.refCount <= 0) {
                storesRef.current.delete(key);
            }
        },
        registerResident: (key, subscriberId, resident) => {
            const entry = storesRef.current.get(key);
            if (entry) {
                entry.residents.set(subscriberId, resident);
            }
        },
        getUnionResident: (key) => {
            const entry = storesRef.current.get(key);
            if (!entry || entry.residents.size === 0)
                return null;
            let minStart = Infinity;
            let maxEnd = -Infinity;
            for (const r of entry.residents.values()) {
                if (!Number.isFinite(r.startMs) || !Number.isFinite(r.endMs))
                    continue;
                if (r.startMs < minStart)
                    minStart = r.startMs;
                if (r.endMs > maxEnd)
                    maxEnd = r.endMs;
            }
            if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd))
                return null;
            return { startMs: minStart, endMs: maxEnd };
        },
        updateResident: (key, subscriberId, resident) => {
            const entry = storesRef.current.get(key);
            if (entry) {
                entry.residents.set(subscriberId, resident);
            }
        },
    }), []);
    return (_jsx(SharedBarStoreContext.Provider, { value: value, children: children }));
}
export function useSharedBarStore() {
    return useContext(SharedBarStoreContext);
}
// Helper hook for charts to automatically manage shared store lifecycle
export function useSharedBarStoreSubscription(key, subscriberId, _initialResident) {
    const ctx = useContext(SharedBarStoreContext);
    const [entry, setEntry] = useState(null);
    useEffect(() => {
        if (!ctx)
            return;
        const e = ctx.getOrCreate(key);
        // Note: resident is NOT registered here — it is set dynamically
        // by Phase 6 (updateResident in useWidgetData) when the actual visible
        // domain is available, preventing a stale zero-width resident from
        // polluting the union computation.
        setEntry({ store: e.store, lane: e.lane });
        return () => {
            ctx.release(key, subscriberId);
            setEntry(null);
        };
    }, [ctx, key, subscriberId]);
    if (!entry)
        return null;
    return { store: entry.store, lane: entry.lane };
}
